import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Pencil, Trash2, Globe, UserPlus, Check, X as XIcon } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

export default function TenantManager() {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', slug: '', domain: '', custom_domain: '', is_active: true, plan: 'basic', membership_enabled: false, documents_enabled: false, payments_enabled: false, wet_signature_enabled: false, resend_api_key: '', sender_email: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAdminForm, setShowAdminForm] = useState(null);
  const [adminForm, setAdminForm] = useState({ name: '', email: '', password: '' });
  const [adminSubmitting, setAdminSubmitting] = useState(false);

  const fetchTenants = useCallback(() => {
    api.get('/tenants').then(r => setTenants(r.data)).catch(err => {
      if (err.response?.status === 403) toast.error('Süper admin yetkisi gerekli');
    }).finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchTenants(); }, [fetchTenants]);

  const resetForm = () => {
    setForm({ name: '', slug: '', domain: '', custom_domain: '', is_active: true, plan: 'basic', membership_enabled: false, documents_enabled: false, payments_enabled: false, wet_signature_enabled: false, resend_api_key: '', sender_email: '' });
    setEditing(null);
    setShowForm(false);
  };

  const parseBool = (v) => v === true || v === 'true' || v === 'True';

  const handleEdit = (t) => {
    setEditing(t);
    setForm({ name: t.name, slug: t.slug, domain: t.domain || '', custom_domain: t.custom_domain || '', is_active: t.is_active !== 'false' && t.is_active !== false, plan: t.plan || 'basic', membership_enabled: parseBool(t.membership_enabled), documents_enabled: parseBool(t.documents_enabled), payments_enabled: parseBool(t.payments_enabled), wet_signature_enabled: parseBool(t.wet_signature_enabled), resend_api_key: t.resend_api_key || '', sender_email: t.sender_email || '' });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.slug.trim()) { toast.error('Ad ve slug gerekli'); return; }
    setIsSubmitting(true);
    try {
      if (editing) {
        await api.put(`/tenants/${editing.id}`, form);
        toast.success('Tenant güncellendi');
      } else {
        await api.post('/tenants', form);
        toast.success('Tenant oluşturuldu');
      }
      resetForm();
      fetchTenants();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Hata oluştu');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu tenant ve tüm verilerini silmek istediğinize emin misiniz?')) return;
    try {
      await api.delete(`/tenants/${id}`);
      toast.success('Tenant silindi');
      fetchTenants();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Silme hatası');
    }
  };

  const handleCreateAdmin = async (tenantId) => {
    if (!adminForm.email || !adminForm.password) { toast.error('E-posta ve şifre gerekli'); return; }
    setAdminSubmitting(true);
    try {
      await api.post(`/tenants/${tenantId}/admin`, adminForm);
      toast.success('Admin kullanıcı oluşturuldu');
      setShowAdminForm(null);
      setAdminForm({ name: '', email: '', password: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Hata oluştu');
    } finally {
      setAdminSubmitting(false);
    }
  };

  return (
    <div data-testid="admin-tenant-manager">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl font-semibold">Tenant Yönetimi</h1>
          <p className="text-sm text-muted-foreground mt-1">Müşteri sitelerini yönetin</p>
        </div>
        <button onClick={() => { resetForm(); setShowForm(true); }}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[hsl(var(--gold))] text-[hsl(var(--accent-foreground))] text-sm font-medium hover:bg-[hsl(var(--gold)/0.92)]">
          <Plus className="w-4 h-4" /> Yeni Tenant
        </button>
      </div>

      {showForm && (
        <div className="mb-8 p-6 rounded-2xl bg-card border border-border shadow-[var(--shadow-elev-1)]">
          <h3 className="font-heading text-lg font-semibold mb-4">{editing ? 'Tenant Düzenle' : 'Yeni Tenant'}</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Firma Adı *" value={form.name} onChange={v => setForm(f => ({ ...f, name: v }))} placeholder="ABC İnşaat" />
              <Field label="Slug *" value={form.slug} onChange={v => setForm(f => ({ ...f, slug: v.toLowerCase().replace(/[^a-z0-9-]/g, '') }))} placeholder="abc-insaat" />
              <Field label="Subdomain" value={form.domain} onChange={v => setForm(f => ({ ...f, domain: v }))} placeholder="abc.ruzgaraltiinsaat.com" />
              <Field label="Custom Domain" value={form.custom_domain} onChange={v => setForm(f => ({ ...f, custom_domain: v }))} placeholder="www.abcinsaat.com" />
              <div>
                <label className="block text-sm font-medium mb-1.5">Plan</label>
                <select value={form.plan} onChange={e => setForm(f => ({ ...f, plan: e.target.value }))}
                  className="w-full px-3 py-2.5 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--gold))]">
                  <option value="basic">Basic</option>
                  <option value="premium">Premium</option>
                  <option value="enterprise">Enterprise</option>
                </select>
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 text-sm pb-2.5">
                  <input type="checkbox" checked={form.is_active} onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))} className="rounded" />
                  Aktif
                </label>
              </div>
            </div>

            {/* Üyelik sistemi ayarları */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 rounded-xl bg-secondary/40 border border-border">
              <label className="flex items-start gap-2.5 text-sm cursor-pointer" data-testid="tenant-membership-toggle">
                <input type="checkbox" checked={form.membership_enabled} onChange={e => setForm(f => ({ ...f, membership_enabled: e.target.checked }))} className="mt-0.5 accent-[hsl(var(--gold))]" />
                <span>
                  <span className="font-medium block">Üyelik Girişi Aktif</span>
                  <span className="text-xs text-muted-foreground">Sitede "Giriş Yap / Kayıt Ol" butonları ve admin panelde "Üyeler" sekmesi görünür.</span>
                </span>
              </label>
              <label className="flex items-start gap-2.5 text-sm cursor-pointer" data-testid="tenant-documents-toggle">
                <input type="checkbox" checked={form.documents_enabled} onChange={e => setForm(f => ({ ...f, documents_enabled: e.target.checked }))} className="mt-0.5 accent-[hsl(var(--gold))]" />
                <span>
                  <span className="font-medium block">Belge Talep Aktif</span>
                  <span className="text-xs text-muted-foreground">Üye profilinde "Belgelerim" sekmesi ve admin panelde "Belge Yönetimi" görünür.</span>
                </span>
              </label>
              <label className="flex items-start gap-2.5 text-sm cursor-pointer md:col-span-2" data-testid="tenant-payments-toggle">
                <input type="checkbox" checked={form.payments_enabled} onChange={e => setForm(f => ({ ...f, payments_enabled: e.target.checked }))} className="mt-0.5 accent-[hsl(var(--gold))]" />
                <span>
                  <span className="font-medium block">Ödeme &amp; Ürün Yönetimi Aktif</span>
                  <span className="text-xs text-muted-foreground">Admin panelde "Ürün Yönetimi", "Ödeme Yönetimi" ve "Ödeme Ayarları" sekmeleri görünür; üyeler profilinden ödeme yapabilir.</span>
                </span>
              </label>
              <label className="flex items-start gap-2.5 text-sm cursor-pointer md:col-span-2" data-testid="tenant-wet-signature-toggle">
                <input type="checkbox" checked={form.wet_signature_enabled} onChange={e => setForm(f => ({ ...f, wet_signature_enabled: e.target.checked }))} className="mt-0.5 accent-[hsl(var(--gold))]" />
                <span>
                  <span className="font-medium block">Islak İmza Belge Yönetimi Aktif</span>
                  <span className="text-xs text-muted-foreground">Admin panelde "Belge Yönetimi" içinde "Islak İmza Belgeleri" görünür; üyeler belgeyi indirir, imzalayıp adrese kargolar.</span>
                </span>
              </label>
            </div>
            {form.membership_enabled && (
              <div className="p-4 rounded-xl bg-secondary/40 border border-border space-y-4">
                <div>
                  <h4 className="text-sm font-medium">E-posta Gönderim Ayarları (Resend)</h4>
                  <p className="text-xs text-muted-foreground mt-0.5">Bu siteye özel Resend bilgileri. Boş bırakılırsa doğrulama/sıfırlama bağlantıları yalnızca ekranda gösterilir (gerçek e-posta gönderilmez).</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1.5">Resend API Key</label>
                    <input type="password" autoComplete="new-password" value={form.resend_api_key} onChange={e => setForm(f => ({ ...f, resend_api_key: e.target.value }))} placeholder="re_xxxxxxxxxxxx"
                      className="w-full px-3 py-2.5 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--gold))]" data-testid="tenant-resend-key" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1.5">Gönderici E-posta (From)</label>
                    <input type="email" value={form.sender_email} onChange={e => setForm(f => ({ ...f, sender_email: e.target.value }))} placeholder="noreply@siteadi.com"
                      className="w-full px-3 py-2.5 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--gold))]" data-testid="tenant-sender-email" />
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">Not: Gönderici alan adının Resend panelinde doğrulanmış olması gerekir.</p>
              </div>
            )}
            <div className="flex gap-3">
              <button type="submit" disabled={isSubmitting}
                className="px-6 py-2.5 rounded-lg bg-[hsl(var(--gold))] text-[hsl(var(--accent-foreground))] text-sm font-medium disabled:opacity-50">
                {isSubmitting ? 'Kaydediliyor...' : 'Kaydet'}
              </button>
              <button type="button" onClick={resetForm} className="px-6 py-2.5 rounded-lg border border-input text-sm hover:bg-secondary">İptal</button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-10"><div className="w-8 h-8 border-2 border-[hsl(var(--gold))] border-t-transparent rounded-full animate-spin" /></div>
      ) : tenants.length === 0 ? (
        <div className="text-center py-10 text-muted-foreground">Henüz tenant yok</div>
      ) : (
        <div className="space-y-3">
          {tenants.map(t => (
            <div key={t.id} className="p-4 rounded-xl bg-card border border-border">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg bg-[hsl(var(--gold)/0.1)] flex items-center justify-center shrink-0">
                  <Globe className="w-5 h-5 text-[hsl(var(--gold))]" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm">{t.name}</div>
                  <div className="text-xs text-muted-foreground">{t.slug} {t.custom_domain ? `| ${t.custom_domain}` : ''}</div>
                </div>
                <span className={`px-2 py-0.5 text-xs rounded-full ${t.is_active === true || t.is_active === 'true' || t.is_active === 'True' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'}`}>
                  {t.is_active === true || t.is_active === 'true' || t.is_active === 'True' ? 'Aktif' : 'Pasif'}
                </span>
                <span className="px-2 py-0.5 text-xs rounded-full bg-[hsl(var(--gold)/0.1)] text-[hsl(var(--gold))]">{t.plan}</span>
                {(t.membership_enabled === true || t.membership_enabled === 'true' || t.membership_enabled === 'True') && (
                  <span className="px-2 py-0.5 text-xs rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">Üyelik</span>
                )}
                {(t.documents_enabled === true || t.documents_enabled === 'true' || t.documents_enabled === 'True') && (
                  <span className="px-2 py-0.5 text-xs rounded-full bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400">Belge</span>
                )}
                {(t.payments_enabled === true || t.payments_enabled === 'true' || t.payments_enabled === 'True') && (
                  <span className="px-2 py-0.5 text-xs rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">Ödeme</span>
                )}
                <div className="flex gap-1">
                  <button onClick={() => setShowAdminForm(showAdminForm === t.id ? null : t.id)} className="p-2 rounded-lg hover:bg-secondary" title="Admin ekle">
                    <UserPlus className="w-4 h-4" />
                  </button>
                  <button onClick={() => handleEdit(t)} className="p-2 rounded-lg hover:bg-secondary"><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => handleDelete(t.id)} className="p-2 rounded-lg hover:bg-destructive/10 text-destructive"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>

              {showAdminForm === t.id && (
                <div className="mt-4 pt-4 border-t border-border">
                  <h4 className="text-sm font-medium mb-3">Bu tenant için admin oluştur</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <input value={adminForm.name} onChange={e => setAdminForm(f => ({ ...f, name: e.target.value }))} placeholder="Ad Soyad"
                      className="px-3 py-2 rounded-lg border border-input bg-background text-sm" />
                    <input type="email" value={adminForm.email} onChange={e => setAdminForm(f => ({ ...f, email: e.target.value }))} placeholder="E-posta *"
                      className="px-3 py-2 rounded-lg border border-input bg-background text-sm" />
                    <input type="password" value={adminForm.password} onChange={e => setAdminForm(f => ({ ...f, password: e.target.value }))} placeholder="Şifre *"
                      className="px-3 py-2 rounded-lg border border-input bg-background text-sm" />
                  </div>
                  <div className="flex gap-2 mt-3">
                    <button onClick={() => handleCreateAdmin(t.id)} disabled={adminSubmitting}
                      className="inline-flex items-center gap-1 px-4 py-2 rounded-lg bg-[hsl(var(--gold))] text-[hsl(var(--accent-foreground))] text-sm font-medium disabled:opacity-50">
                      <Check className="w-3.5 h-3.5" /> {adminSubmitting ? 'Oluşturuluyor...' : 'Oluştur'}
                    </button>
                    <button onClick={() => setShowAdminForm(null)}
                      className="inline-flex items-center gap-1 px-4 py-2 rounded-lg border border-input text-sm">
                      <XIcon className="w-3.5 h-3.5" /> İptal
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const Field = ({ label, value, onChange, placeholder }) => (
  <div>
    <label className="block text-sm font-medium mb-1.5">{label}</label>
    <input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
      className="w-full px-3 py-2.5 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--gold))]" />
  </div>
);
